import { useCallback, useMemo } from 'react';

import {
    PLEASE_SELECT_QHP_ID,
    PLEASE_SELECT_QHP_OBJECT,
    UNASSIGNED_QHP_OBJECT,
} from '../../../constants/visit';
import { Assignee, JointAssignment } from '../../../fetching/api/availability';
import { QhpSimple } from '../../../fetching/sharedSchemas/basicSchemas';
import { InputState, useInputState, useTransformedInputState } from '../../../utils/inputState';
import { ReturnComponent } from '../../../utils/reactTypes';
import { AxleAutocompleteField } from '../../formFields/AxleAutocompleteField';
import { QhpLabel } from '../../formFields/QhpSelect';
import { QhpGroup, groupForQhp, isSentinelQhpId } from './eligibility';

// This is a low-level function to not be used directly.
//  It's appropriate for situations where positions will be created or deleted.
export const useAssigneeStateHelper = (qhp: Assignee) => {
    return useInputState(qhp, {
        required: true,
        validators: [
            (newQhp) =>
                newQhp.uuid === PLEASE_SELECT_QHP_ID ? 'Please select an assignee.' : null,
        ],
    });
};

// This is a low-level function to not be used directly.
//  It's appropriate for situations where positions won't be created or deleted.
export const useAssigneesStateHelper = ({
    assignment,
    positionIdsToForceReassign,
    onChangeCallbacks,
}: {
    assignment: JointAssignment;
    positionIdsToForceReassign: string[];
    onChangeCallbacks: ((assignment: JointAssignment) => void)[];
}): InputState<JointAssignment> => {
    const initialValue = {
        ...assignment,
        ...Object.fromEntries(
            positionIdsToForceReassign.map((positionId) => [positionId, PLEASE_SELECT_QHP_OBJECT]),
        ),
    };
    const validateNoPleaseSelectObjects = (value: JointAssignment) =>
        Object.values(value).some((assignee) => assignee === PLEASE_SELECT_QHP_OBJECT)
            ? 'Please select an assignee for each clinician appointment.'
            : null;
    return useInputState(initialValue, {
        validators: [validateNoPleaseSelectObjects],
        onChangeCallbacks,
    });
};

export const usePotentialAssigneesStateHelper = (positions: { uuid: string }[]) => {
    const assignment: JointAssignment = Object.fromEntries(
        positions.map((position) => [position.uuid, PLEASE_SELECT_QHP_OBJECT]),
    );
    return useAssigneesStateHelper({
        assignment,
        positionIdsToForceReassign: [],
        onChangeCallbacks: [],
    });
};

export type AssigneesState = ReturnType<typeof useAssigneesStateHelper>;

// "Dumb" meaning it doesn't make any queries to the backend automatically.
export const DumbAssigneeSelect = ({
    label,
    enabled = true,
    inputState,
    careTeam,
    assignableQhps,
    eligibleQhps,
    availableQhps,
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<Assignee>;
    careTeam: { id: string }[];
    assignableQhps: QhpSimple[];
    eligibleQhps: QhpSimple[];
    // There are times we don't want to distinguish by availability, e.g.,
    //  when a visit is in the past. In that case, we will only show eligibility.
    //  We know to do this if `availableQhps` is null.
    availableQhps: QhpSimple[] | null;
}): ReturnComponent => {
    // Under the hood, we use only the UUID of the QHP to handle selection, because our
    // incoming/initial assignee and assignee options come from different sources, so
    // they do not have object equality.
    const assigneeUuidInputState = useTransformedInputState({
        inputState,
        transform: (qhp) => qhp.uuid,
        untransform: (uuid) =>
            assignableQhps.find((qhp) => qhp.uuid === uuid) ?? UNASSIGNED_QHP_OBJECT,
    });
    const careTeamQhpIds = useMemo(
        () => new Set(careTeam.map((clinician) => clinician.id)),
        [careTeam],
    );
    const eligibleQhpIds = useMemo(
        () => new Set(eligibleQhps.map((qhp) => qhp.uuid)),
        [eligibleQhps],
    );
    const availableQhpIds = useMemo(
        () => new Set(availableQhps?.map((qhp) => qhp.uuid) ?? []),
        [availableQhps],
    );
    const getGroupOfQhp = useCallback(
        (qhp: QhpSimple): QhpGroup => {
            if (isSentinelQhpId(qhp.uuid)) {
                return { name: '', order: -1 };
            }
            return groupForQhp({
                isCareTeam: careTeamQhpIds.has(qhp.uuid),
                isEligible: eligibleQhpIds.has(qhp.uuid),
                isAvailable: availableQhpIds.has(qhp.uuid),
                availabilityKnown: availableQhps !== null,
            });
        },
        [availableQhpIds, availableQhps, eligibleQhpIds, careTeamQhpIds],
    );

    const groupFromQhpId: Record<string, QhpGroup> = useMemo(
        () =>
            Object.fromEntries(
                [UNASSIGNED_QHP_OBJECT, PLEASE_SELECT_QHP_OBJECT]
                    .concat(assignableQhps)
                    .map((qhp) => [qhp.uuid, getGroupOfQhp(qhp)]),
            ),
        [assignableQhps, getGroupOfQhp],
    );

    const options = useMemo(
        () =>
            // If there's no options, this means we are waiting for remote results to load.
            //  In this case, we want to NOT include the UNASSIGNED_QHP_OBJECT as then the
            //  noOptionsText we have customized below won't show.
            assignableQhps.length === 0
                ? []
                : // Always include the "Unassigned" option at the top of the list.
                  [UNASSIGNED_QHP_OBJECT].concat(
                      // Sort QHPs by group first, then by name.
                      // Create a copy before sorting to avoid mutating the original and triggering unnecessary re-renders.
                      [...assignableQhps].sort((qhp1, qhp2) => {
                          const groupOrder1 = groupFromQhpId[qhp1.uuid].order;
                          const groupOrder2 = groupFromQhpId[qhp2.uuid].order;
                          return groupOrder1 === groupOrder2
                              ? qhp1.name.localeCompare(qhp2.name)
                              : groupOrder1 - groupOrder2;
                      }),
                  ),
        [assignableQhps, groupFromQhpId],
    );

    return (
        <AxleAutocompleteField
            label={label}
            enabled={enabled}
            inputState={assigneeUuidInputState}
            tooltipText={label}
            options={options}
            optionToValue={(qhp) => qhp.uuid}
            optionToLabel={(qhp) => qhp.name}
            renderOption={(qhp) => <QhpLabel option={qhp} />}
            // Disable the default of sorting by name, because we already sorted options by
            //  assignability first, name second.
            sortOptions={false}
            groupBy={(qhp) => groupFromQhpId[qhp.uuid].name}
            // Indicate to the user when new options are being fetched.
            noOptionsText={'Fetching latest availability...'}
            disableResetWithoutInput={true}
        />
    );
};
